import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Radio, RadioGroup } from '@/components/Radio.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Description, Header1, Header3 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

const StyledRadioGroup = styled(RadioGroup)`
  margin: 20px 0;
`

const Question = styled(Header3)`
  margin-top: 16px;
  font-size: 20px;
  line-height: 26px;
  white-space: pre-line;
  text-align: left;
`

const StyledFullScreenContainer = styled(FullScreenContainer)`
  text-align: left;
`

export const MoreInformation = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'moreInformation',
  })
  const { trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.FillMoreInformation,
  )
  const navigate = useNavigate()

  const { questionnaire, setAppointmentType } = useAppointmentStore(
    (state) => ({
      questionnaire: state.questionnaire,
      setAppointmentType: state.setAppointmentType,
    }),
  )
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null)

  if (!questionnaire) {
    return null
  }

  return (
    <ScreenWrapper>
      <StyledFullScreenContainer align="center">
        <Header1>{t('header')}</Header1>
        <Description>{t('description')}</Description>
        <ScrollableContent>
          <Question>{questionnaire.question}</Question>
          <StyledRadioGroup
            name="answer"
            value={selectedAnswerId}
            onChange={(value: string) => {
              setSelectedAnswerId(value)
              const newAppointmentType = questionnaire.answers.find(
                (answer) => answer.id === value,
              )?.value
              if (newAppointmentType) {
                setAppointmentType(newAppointmentType)
              }
            }}
            id="bookingQuestionnaire"
            onClick={() => {
              trackFormInteraction({
                action: 'click',
                formFieldName: 'bookingQuestionnaire',
                formFieldValues: questionnaire.answers.map(
                  (answer) => answer.text,
                ),
              })
            }}
          >
            {questionnaire.answers.map((answer) => (
              <Radio key={answer.value} value={answer.id} text={answer.text} />
            ))}
          </StyledRadioGroup>
        </ScrollableContent>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={!selectedAnswerId}
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
              navigate('/referral/confirm-details')
            }}
          >
            {t('actions.continue')}
          </StyledActionButton>
        </ButtonGroup>
      </StyledFullScreenContainer>
    </ScreenWrapper>
  )
}
